import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {propOr, mergeRight} from 'ramda';
import useForceUpdate from 'use-force-update';

import {getFontSize} from '../utils';

/**
 * An app-level footer component.
 *
 * **Example Usage**
 * ```
ddk.Footer(
    title="High Dimensional Plotting App",
    logo="https://dash.plotly.com/assets/images/plotly_logo_dark.png",
    tags=[
        ddk.Tag(text="**App Version:** v1.0.0", icon="code-compare"),
        ddk.Tag(text="**App Data Updated:** Sept. 14, 2023", icon="calendar"),
        ddk.Tag(text="**App Team:** Research Branch III", icon="people-group")
    ],
)
 * ```
 */
function Footer(props) {
    const {
        id,
        title,
        logo,
        tags,
        children,
        margin,
        padding,
        background_color,
        border_width,
        border_style,
        border_color,
        border_radius,
        box_shadow,
        font_color,
        font_family,
        font_size,
    } = props;

    const forceUpdate = useForceUpdate();

    const handleThemeUpdate = () => {
        forceUpdate();
    };

    useEffect(() => {
        window.addEventListener('dash-theme-update', handleThemeUpdate);
        return () => {
            // unsubscribe event
            window.removeEventListener('dash-theme-update', handleThemeUpdate);
        };
    }, []);

    /* merge conditional styles from props */
    const style = mergeRight(
        {
            padding: padding,
            margin: margin,
            backgroundColor: background_color,
            borderWidth: border_width,
            borderStyle: border_style,
            borderColor: border_color,
            borderRadius: border_radius,
            boxShadow: box_shadow,
            color: font_color,
            fontFamily: font_family,
            fontSize: getFontSize(font_size),
        },
        propOr({}, 'style', props)
    );
    return (
        <footer
            className={`layout-footer-container ${propOr('', 'className', props)}`}
            id={id}
            style={style}
        >
            <div className="footer-content">
                <div className="footer-leading">
                    {title && <div className="footer-title">{title}</div>}
                    {tags && (
                        <div className="footer-tags">
                            {tags?.map((tag, index) => {
                                return <div key={tag.key ?? index}>{tag}</div>;
                            })}
                        </div>
                    )}
                </div>
                {logo && (
                    <div className="footer-trailing">
                        <div className="footer-logo-container">
                            <img src={logo} alt={`Logo path: "${logo}`} />
                        </div>
                    </div>
                )}
                {children}
            </div>
        </footer>
    );
}

Footer.propTypes = {
    /**
     * The ID of this component, used to identify Dash components
     * in callbacks. The ID needs to be unique across all of the
     * components in an app.
     */
    id: PropTypes.string,

    /**
     * Optional user-defined CSS class for the footer
     */
    className: PropTypes.string,

    /**
     * The contents of the footer.
     */
    children: PropTypes.node,

    /**
     * Optional prop to specify the source (src) of an image that represents a logo within the component.
     * This prop should be assigned a string value containing the URL or file path to the logo image.
     */
    logo: PropTypes.string,

    /**
     * Prop to set the app title or heading displayed on the footer.
     */
    title: PropTypes.string,

    /**
     * This should be an array of `ddk.Tag` elements.
     */
    tags: PropTypes.node,

    /**
     * Space (in pixels) surrounding the footer.
     * Overrides theme.header_margin.
     */
    margin: PropTypes.number,

    /**
     * Space (in pixels) on the inside of the footer, between the border
     * and the edge of the content.
     * Overrides theme.header_padding.
     */
    padding: PropTypes.number,

    /**
     * The background color applied to the footer. Overrides theme.header_background_color.
     */
    background_color: PropTypes.string,

    /**
     * The box shadow(s) applied to the footer. Overrides theme.header_box_shadow.
     */
    box_shadow: PropTypes.string,

    /**
     * The border width applied to the footer. Overrides theme.header_border.width.
     */
    border_width: PropTypes.string,

    /**
     * The border style applied to the footer. Overrides theme.header_border.style.
     */
    border_style: PropTypes.string,

    /**
     * The border color applied to the footer. Overrides theme.header_border.color.
     */
    border_color: PropTypes.string,

    /**
     * The border radius applied to the footer. Overrides theme.header_border.radius.
     */
    border_radius: PropTypes.string,

    /**
     * The font color of Footer children. Overrides theme.header_text
     */
    font_color: PropTypes.string,

    /**
     * The font family of Footer children. Overrides theme.font_family_header
     */
    font_family: PropTypes.string,

    /**
     * The font size of Footer children.
     * Can be a relative size, "smaller", "normal", "larger",
     * or a CSS attribute size (e.g. `px`, 'em`)
     */
    font_size: PropTypes.string,

    /**
     * The style object of the outermost div of the Footer.
     * Use this to override e.g. the Footer's height
     * (`{'height': '100px'}`)
     */
    style: PropTypes.object,
};

export default Footer;
